import {useState} from 'react';
import {getStore} from 'src/v2/state/store';
import {useWalletConnectionClient} from 'src/v2/state/walletConnection/useWalletConnectionClient';
import {getDepositAddress} from 'src/v2/components/pay/getDepositAddress';
import type {SupportedCurrency} from 'src/v2/state/types';
import {useUpdateSessionOnExchangeSelect} from 'src/v2/components/exchangeScreen/useUpdateSessionOnExchangeSelect';
import {useExchangePaymentPolling} from 'src/v2/components/exchangeScreen/useExchangePaymentPolling';
import {useSingleRunEffect} from 'src/v2/util/useSingleRunEffect';
import {useIntl} from '@sail/intl';
import {handleError} from 'src/v2/components/error/handleError';

export const useInitializeExchangePayment = (): {
  paymentUrl: string | null;
  paymentQrCode: string | null;
} => {
  const store = getStore();
  const intl = useIntl();
  const walletClient = useWalletConnectionClient();
  const updateSessionOnExchangeSelect = useUpdateSessionOnExchangeSelect();
  const {startPolling} = useExchangePaymentPolling();

  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [paymentQrCode, setPaymentQrCode] = useState<string | null>(null);

  const externalPaymentDetails = store.useSelector(
    (state) => state.externalPaymentDetails,
  );
  const depositAddresses = store.useSelector((state) => state.depositAddresses);
  const usdcSolana = store.useSelector((state) =>
    state.supportedCurrencies.find((currency) => currency.id === 'usdc.solana'),
  ) as SupportedCurrency;

  const depositAddress = getDepositAddress({
    selectedAsset: usdcSolana,
    depositAddresses,
  });

  const currency = store.useSelector((state) => state.paymentDetails.currency);
  const amount = store.useSelector((state) => state.paymentDetails.amount);
  const merchantName = store.useSelector(
    (state) => state.merchantBranding.businessName,
  );
  const cpis = store.useSelector((state) => state.id);

  useSingleRunEffect(() => {
    if (externalPaymentDetails) {
      setPaymentUrl(externalPaymentDetails.paymentUrl);
      setPaymentQrCode(externalPaymentDetails.paymentQrCode);
      startPolling();
      return;
    }

    const createExchangePayment = async () => {
      try {
        const formattedAmount = currency === 'usdc' ? amount / 10000 : amount;
        const {
          url,
          qrCode: qrCodeData,
          paymentId,
        } = await walletClient.createExchangePayment({
          exchange: 'cryptoDotCom',
          address: depositAddress,
          token: 'USDC',
          chainName: 'SOL',
          network: '0',
          currency: 'USD',
          tokenAmount: formattedAmount,
          merchantName: merchantName ?? undefined,
          description: `Stripe Crypto Payment ${cpis}`,
        });

        setPaymentUrl(url);
        setPaymentQrCode(qrCodeData);

        updateSessionOnExchangeSelect({
          paymentUrl: url,
          paymentQrCode: qrCodeData,
          paymentId,
        });

        startPolling();
      } catch (error) {
        handleError({error, intl, context: 'exchange_payment'});
      }
    };

    createExchangePayment();
  });

  return {paymentUrl, paymentQrCode};
};
